/*
 * Search metadata for the property and agent location pages. The page applies it from its
 * own URL and listings; the pre-renderer captures the result, so crawlers and browsers see
 * the same tags. A page is indexable only for a valid province and a non-administrative city,
 * without query overrides, and while it shows enough listings that belong to its location
 * (property pages: a city and at least MIN_PROPERTY_LISTINGS; agent pages: at least one). All writes are text-only
 * (document.title, setAttribute, textContent) and upsert by key, so repeated renders are safe.
 */
import { canonicalLocationPath, getLocationPage, isAdministrativeLocation, LocationLanguage, LocationPage, MIN_PROPERTY_LISTINGS, PROVINCES } from './location-path';

export const SITE_ORIGIN = 'https://www.royallepagecommercial.com';
const BRAND = 'Royal LePage Commercial';

const COPY = {
  properties: {
    en: { title: (place: string) => `Commercial Real Estate for Sale and Lease in ${place}`, description: (place: string) => `Browse commercial real estate for sale and lease in ${place} with ${BRAND}.` },
    fr: { title: (place: string) => `Immobilier commercial à vendre et à louer à ${place}`, description: (place: string) => `Parcourez l'immobilier commercial à vendre et à louer à ${place} avec ${BRAND}.` },
  },
  agents: {
    en: { title: (place: string) => `Commercial Real Estate Agents in ${place}`, description: (place: string) => `Find commercial real estate agents in ${place} with ${BRAND}.` },
    fr: { title: (place: string) => `Courtiers immobiliers commerciaux à ${place}`, description: (place: string) => `Trouvez des courtiers immobiliers commerciaux à ${place} avec ${BRAND}.` },
  },
};

export interface LocationSeo {
  robots: string;
  title?: string;
  description?: string;
  canonical?: string;
  alternates?: Record<'en' | 'fr' | 'x-default', string>;
}

const words = (slug: string) => slug.split('-').filter(Boolean)
  .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

function placeName(page: LocationPage): string {
  const province = page.province.toUpperCase();
  return page.city ? `${words(page.city)}, ${province}` : province;
}

function hasValidLocation(page: LocationPage | null): page is LocationPage {
  return !!page && PROVINCES.includes(page.province) && !isAdministrativeLocation(page.city);
}

function hasEnoughListings(page: LocationPage, listings: number): boolean {
  return page.kind === 'properties' ? !!page.city && listings >= MIN_PROPERTY_LISTINGS : listings > 0;
}

// `listings` counts listings shown that belong to the location (0 for agents' nearby fallback).
export function buildLocationSeo(pathname: string, search: string, listings: number): LocationSeo {
  const page = getLocationPage(pathname);
  if (!hasValidLocation(page)) { return { robots: 'noindex, follow' }; }
  const params = new URLSearchParams(search);
  const overridden = ['keyword', 'name'].some(name => (params.get(name) || '').trim());
  const place = placeName(page);
  const copy = COPY[page.kind][page.language];
  const seo: LocationSeo = {
    robots: !overridden && hasEnoughListings(page, listings) ? 'index, follow' : 'noindex, follow',
    title: `${copy.title(place)} | ${BRAND}`,
    description: copy.description(place),
  };
  if (seo.robots === 'index, follow') {
    const url = (language: LocationLanguage) =>
      SITE_ORIGIN + canonicalLocationPath(page.kind, language, page.province, page.city);
    seo.canonical = url(page.language);
    seo.alternates = { en: url('en'), fr: url('fr'), 'x-default': url('en') };
  }
  return seo;
}

function upsert(document: Document, selector: string, tag: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement(tag);
    document.head.appendChild(element);
  }
  // Skip unchanged values: repeated renders must not create DOM mutations (the pre-renderer
  // waits for mutations to settle).
  Object.entries(attributes).forEach(([name, value]) => {
    if (element.getAttribute(name) !== value) { element.setAttribute(name, value); }
  });
}

function remove(document: Document, selector: string) {
  document.head.querySelectorAll(selector).forEach(element => element.remove());
}

export function applyLocationSeo(document: Document, seo: LocationSeo) {
  upsert(document, 'meta[name="robots"]', 'meta', { name: 'robots', content: seo.robots });
  if (seo.title) {
    if (document.title !== seo.title) { document.title = seo.title; }
    upsert(document, 'meta[property="og:title"]', 'meta', { property: 'og:title', content: seo.title });
    upsert(document, 'meta[property="og:type"]', 'meta', { property: 'og:type', content: 'website' });
  }
  if (seo.description) {
    upsert(document, 'meta[name="description"]', 'meta', { name: 'description', content: seo.description });
    upsert(document, 'meta[property="og:description"]', 'meta', { property: 'og:description', content: seo.description });
  }
  if (seo.canonical && seo.alternates) {
    upsert(document, 'link[rel="canonical"]', 'link', { rel: 'canonical', href: seo.canonical });
    upsert(document, 'meta[property="og:url"]', 'meta', { property: 'og:url', content: seo.canonical });
    Object.entries(seo.alternates).forEach(([hreflang, href]) =>
      upsert(document, `link[rel="alternate"][hreflang="${hreflang}"]`, 'link', { rel: 'alternate', hreflang, href }));
  } else {
    remove(document, 'link[rel="canonical"], link[rel="alternate"][hreflang], meta[property="og:url"]');
  }
}

// Location heading text comes only from slugs and is written as text, never as markup.
export function setLocationHeading(document: Document, headingSelector: string, pathname: string) {
  const target = document.querySelector(`${headingSelector} .location-title-place`);
  if (!target) { return; }
  const page = getLocationPage(pathname);
  const text = page && page.province ? placeName(page).toUpperCase() : '';
  if (target.textContent !== text) { target.textContent = text; }
}
